<script>
import { mapActions } from 'vuex';
import request from '@/services/request';

// Modal de detalle EXCLUSIVO de CFDI de extras sin contrato.
// Se abre con Event.$emit('event-show-extra-invoice-details', buy).
export default {
    name: 'ModalShowBillExtraWithoutContractComponent',

    data() {
        return {
            extra: {},
            bills: [],
            load: false,
            send: false,
            cancellingBillId: null,
            loadError: '',
            warnings: [],
            opened: false,
            fetchNumber: 0,
        };
    },

    methods: {
        ...mapActions({
            errorNotification: 'alert/error',
            successNotification: 'alert/success',
        }),

        num(value) {
            const n = Number(value);
            return Number.isFinite(n) ? n : 0;
        },

        money(value) {
            return this.num(value).toLocaleString('es-MX', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },

        first(values, fallback = '—') {
            const value = values.find(value => value !== null && value !== undefined && value !== '');
            return value === undefined ? fallback : value;
        },

        invoiceJson(bill) {
            if (!bill || !bill.json) return {};
            if (typeof bill.json === 'object') return bill.json;
            try { return JSON.parse(bill.json) || {}; } catch (error) { return {}; }
        },

        deep(data, path) {
            return path.split('.').reduce((current, key) => current == null ? undefined : current[key], data);
        },

        getValue(bill, paths, fallback = '—') {
            const json = this.invoiceJson(bill);
            return this.first(paths.map(path => {
                return path.startsWith('json.') ? this.deep(json, path.substring(5)) : this.deep(bill, path);
            }), fallback);
        },

        folio(bill) {
            const series = this.getValue(bill, ['series', 'json.series'], '');
            const number = this.getValue(bill, ['folio_number', 'folio', 'json.folio_number', 'json.folio'], '');
            return [series, number].filter(Boolean).join('-') || `#${bill.id}`;
        },

        uuid(bill) {
            // Nunca utilizar json.id: es el ID de Facturapi, NO el UUID del SAT.
            return this.getValue(bill, ['uuid', 'json.uuid'], 'Sin UUID registrado');
        },

        currency(bill) {
            const code = this.getValue(bill, ['currency', 'json.currency'], '');
            if (code) return String(code).toUpperCase();
            return Number(bill.currency_id) === 2 ? 'USD' : 'MXN';
        },

        exchange(bill) {
            return this.getValue(bill, ['exchange_rate_bill', 'json.exchange', 'json.exchange_rate'], null);
        },

        customerName(bill) {
            return this.getValue(bill, [
                'billing_data.business_name', 'json.customer.legal_name', 'json.customer.name',
            ], 'Sin razón social');
        },

        customerRfc(bill) {
            return this.getValue(bill, ['billing_data.rfc', 'json.customer.tax_id'], '—');
        },

        customerRegime(bill) {
            return this.getValue(bill, ['json.customer.tax_system'], '—');
        },

        customerZip(bill) {
            return this.getValue(bill, ['json.customer.address.zip'], '—');
        },

        cfdiUse(bill) {
            return this.getValue(bill, ['json.customer.default_invoice_use', 'json.invoice_use'], '—');
        },

        paymentMethod(bill) {
            const code = this.getValue(bill, ['payment_method', 'json.payment_method'], '—');
            const names = { PUE: 'Pago en una sola exhibición', PPD: 'Pago diferido o parcialidades' };
            return names[code] ? `${code} · ${names[code]}` : code;
        },

        paymentForm(bill) {
            const code = this.getValue(bill, ['payment_form', 'json.payment_form'], '—');
            const names = {
                '01': 'Efectivo', '02': 'Cheque nominativo',
                '03': 'Transferencia electrónica', '04': 'Tarjeta de crédito',
                '28': 'Tarjeta de débito', '99': 'Por definir',
            };
            return names[code] ? `${code} · ${names[code]}` : code;
        },

        status(bill) {
            const code = String(this.getValue(bill, ['status', 'json.status'], 'valid')).toLowerCase();
            const cancel = String(this.getValue(bill, ['json.cancellation_status', 'cancellation_status'], '')).toLowerCase();
            if (['canceled', 'cancelled', 'cancelado'].includes(code)) return 'canceled';
            if (['pending', 'verifying'].includes(cancel) || ['pending_cancel', 'pending_cancellation'].includes(code)) return 'pending';
            if (code !== 'valid') return 'other';
            return 'valid';
        },

        statusLabel(bill) {
            const labels = { canceled: 'Cancelada', pending: 'Cancelación pendiente', valid: 'Vigente', other: 'Verificar estado' };
            return labels[this.status(bill)];
        },

        statusClass(bill) {
            return {
                'invoice-status--valid': this.status(bill) === 'valid',
                'invoice-status--canceled': this.status(bill) === 'canceled',
                'invoice-status--pending': ['pending', 'other'].includes(this.status(bill)),
            };
        },

        lines(bill) {
            return Array.isArray(bill.items) ? bill.items : [];
        },

        lineAmount(item) {
            return this.num(item.final_amount);
        },

        lineSubtotal(item) {
            return this.num(item.subtotal);
        },

        totalItems(bill, key) {
            return this.lines(bill).reduce((sum, item) => sum + this.num(item[key]), 0);
        },

        invoiceSubtotal(bill) {
            const value = this.getValue(bill, ['subtotal', 'json.subtotal'], null);
            return value === null ? this.totalItems(bill, 'subtotal') : this.num(value);
        },

        invoiceTotal(bill) {
            const value = this.getValue(bill, ['total', 'json.total'], null);
            return value === null ? this.totalItems(bill, 'total') : this.num(value);
        },

        clientName() {
            const user = this.extra && this.extra.user || {};
            return [user.name, user.last_name].filter(Boolean).join(' ') || 'Cliente';
        },

        // Usa la misma base URL de descarga que el modal de contratos.
        fileUrl(bill, format) {
            return `${String(this.globalUrlFiles).replace(/\/$/, '')}/file/extra_due_invoice/${bill.id}/${format}`;
        },

        async openModal(extra) {
            if (!extra || !extra.id || extra.source_type === 'contract') return;
            this.extra = extra;
            this.bills = Array.isArray(extra.bill_external) ? extra.bill_external : [];
            this.loadError = '';
            this.warnings = [];
            this.opened = true;
            this.$modal.show('show-bill-extra-without-contract');
            await this.list();
        },

        async list() {
            if (!this.extra || !this.extra.id) return;
            const requestNo = ++this.fetchNumber;
            this.load = true;
            this.loadError = '';
            try {
                const { data } = await request.get(`/extra_dues/${this.extra.id}/invoices`);
                if (!this.opened || requestNo !== this.fetchNumber) return;
                this.bills = Array.isArray(data.invoices) ? data.invoices : [];
                this.warnings = Array.isArray(data.warnings) ? data.warnings : [];
                if (data.extra_due) this.extra = Object.assign({}, this.extra, data.extra_due);
            } catch (error) {
                if (!this.opened || requestNo !== this.fetchNumber) return;
                this.loadError = error && error.response && error.response.data && error.response.data.message
                    || 'No se pudo actualizar el historial. Vuelve a intentar.';
            } finally {
                if (requestNo === this.fetchNumber) this.load = false;
            }
        },

        // Cancelación rápida, igual que el detalle de facturas con contrato:
        // solo una confirmación. El motivo SAT 02 aplica a errores SIN relación
        // con otra factura. Para motivo 01/sustitución se necesita un flujo aparte.
        async cancelInvoice(bill) {
            if (!bill || this.status(bill) !== 'valid' || this.send) return;

            const confirmed = await this.$swal({
                title: 'Cancelar factura',
                text: `¿Deseas solicitar la cancelación de la factura ${this.folio(bill)}? Motivo SAT 02: errores sin relación con otra factura.`,
                icon: 'warning',
                showCancelButton: true,
                reverseButtons: true,
                confirmButtonColor: '#d84d5d',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Sí, cancelar factura',
                cancelButtonText: 'Regresar',
            });

            if (!confirmed.isConfirmed) return;

            this.send = true;
            this.cancellingBillId = bill.id;

            try {
                const { data } = await request.post(
                    `/extra_dues/${this.extra.id}/invoices/${bill.id}/cancel`,
                    { motive: '02' }
                );

                this.successNotification(data.message);
                await this.list();
                Event.$emit('event-load-extra-dues-without-contract');
            } catch (error) {
                await this.errorNotification(error);
                // Si Facturapi respondió tarde, refrescar antes de reintentar.
                await this.list();
            } finally {
                this.send = false;
                this.cancellingBillId = null;
            }
        },

        closeModal() {
            if (this.send) return;
            this.opened = false;
            this.fetchNumber++;
            this.$modal.hide('show-bill-extra-without-contract');
            this.bills = [];
            this.extra = {};
            this.loadError = '';
            this.warnings = [];
        },
    },

    created() {
        Event.$on('event-show-extra-invoice-details', this.openModal);
    },

    beforeDestroy() {
        Event.$off('event-show-extra-invoice-details', this.openModal);
        this.fetchNumber++;
    },
};
</script>

<template>
    <modal name="show-bill-extra-without-contract" :width="'92%'" height="auto"
        :adaptive="true" :scrollable="true" :click-to-close="false">
        <div class="invoice-history-modal invoice-history-modal--independent">
            <div class="invoice-history-modal__header">
                <div class="invoice-history-modal__title-group">
                    <div class="invoice-history-modal__icon"><i class="fa-solid fa-file-invoice-dollar"></i></div>
                    <div>
                        <h4 class="invoice-history-modal__title">Facturas de cargo sin contrato</h4>
                        <p class="invoice-history-modal__subtitle mb-0">CFDI, receptor, conceptos, impuestos y documentos fiscales.</p>
                    </div>
                </div>
                <div class="invoice-history-modal__header-actions">
                    <span class="invoice-history-modal__debt">Cargo #{{ extra.id }}</span>
                    <button type="button" class="invoice-history-modal__close" :disabled="send"
                        aria-label="Cerrar" @click="closeModal"><i class="fas fa-times"></i></button>
                </div>
            </div>

            <div v-if="load" class="invoice-history-loading">
                <div class="invoice-history-loading__spinner"><i class="fas fa-circle-notch fa-spin"></i></div>
                <h5>Actualizando facturas</h5><p>Consultando el historial del cargo independiente.</p>
            </div>
            <div v-else class="invoice-history-modal__body">
                <div v-if="loadError" class="alert alert-danger" role="alert">
                    {{ loadError }}
                    <button type="button" class="btn btn-sm btn-outline-danger ml-2" @click="list">Reintentar</button>
                </div>
                <div v-for="(warning, wi) in warnings" :key="wi" class="alert alert-warning" role="alert">{{ warning }}</div>

                <div class="invoice-history-summary">
                    <div class="invoice-history-summary__item">
                        <small>Facturas registradas</small><strong>{{ bills.length }}</strong>
                    </div>
                    <div class="invoice-history-summary__item">
                        <small>Cargo extraordinario</small><strong>#{{ extra.id }}</strong>
                    </div>
                    <div class="invoice-history-summary__item">
                        <small>Cliente</small><strong>{{ clientName() }}</strong>
                    </div>
                </div>

                <div v-if="!bills.length" class="invoice-history-empty">
                    <div class="invoice-history-empty__icon"><i class="fa-regular fa-file-lines"></i></div>
                    <h5>Sin facturas registradas</h5><p>No se encontraron CFDI para este cargo.</p>
                </div>

                <div v-else class="invoice-history-list">
                    <div v-for="(bill, index) in bills" :key="bill.id || index" class="invoice-record">
                        <div class="invoice-record__header">
                            <div class="invoice-record__identity">
                                <div class="invoice-record__number">{{ index + 1 }}</div>
                                <div class="invoice-record__heading">
                                    <div class="invoice-record__title-row">
                                        <strong>Factura {{ folio(bill) }}</strong>
                                        <span class="invoice-status" :class="statusClass(bill)">
                                            <i class="fa-solid mr-1"
                                                :class="status(bill) === 'canceled' ? 'fa-ban' : status(bill) === 'pending' ? 'fa-clock' : 'fa-circle-check'"></i>
                                            {{ statusLabel(bill) }}
                                        </span>
                                    </div>
                                    <small>UUID: <span class="invoice-uuid">{{ uuid(bill) }}</span></small>
                                    <small>ID Facturapi: <span class="invoice-uuid">{{ bill.external_id || '—' }}</span></small>
                                </div>
                            </div>
                            <div class="invoice-record__date">
                                <small>Fecha de emisión</small>
                                <strong>{{ getValue(bill, ['json.date', 'created_at', 'json.created_at']) | toDateTime }}</strong>
                            </div>
                        </div>

                        <div class="invoice-record__body">
                            <div class="invoice-info-grid">
                                <div class="invoice-info-card invoice-info-card--wide">
                                    <span class="invoice-info-card__icon"><i class="fa-solid fa-user"></i></span>
                                    <div><small>Receptor</small><strong :title="customerName(bill)">{{ customerName(bill) }}</strong>
                                        <span>RFC {{ customerRfc(bill) }}</span></div>
                                </div>
                                <div class="invoice-info-card">
                                    <span class="invoice-info-card__icon"><i class="fa-solid fa-money-bill-transfer"></i></span>
                                    <div><small>Moneda</small><strong>{{ currency(bill) }}</strong></div>
                                </div>
                                <div class="invoice-info-card">
                                    <span class="invoice-info-card__icon"><i class="fa-solid fa-arrow-right-arrow-left"></i></span>
                                    <div><small>Tipo de cambio</small><strong>{{ exchange(bill) === null ? '—' : exchange(bill) }}</strong></div>
                                </div>
                                <div class="invoice-info-card">
                                    <span class="invoice-info-card__icon"><i class="fa-solid fa-file-code"></i></span>
                                    <div><small>CFDI</small><strong>Versión {{ getValue(bill, ['cfdi_version', 'json.cfdi_version'], '4.0') }}</strong></div>
                                </div>
                            </div>

                            <div class="invoice-payment-info invoice-payment-info--extended">
                                <div><small>Método de pago</small><strong>{{ paymentMethod(bill) }}</strong></div>
                                <div><small>Forma de pago</small><strong>{{ paymentForm(bill) }}</strong></div>
                                <div><small>Régimen receptor</small><strong>{{ customerRegime(bill) }}</strong></div>
                                <div><small>Uso CFDI</small><strong>{{ cfdiUse(bill) }}</strong></div>
                                <div><small>CP fiscal receptor</small><strong>{{ customerZip(bill) }}</strong></div>
                                <div><small>Empresa emisora</small><strong>{{ extra.company && extra.company.name || '—' }}</strong><small v-if="extra.company && extra.company.rfc">RFC {{ extra.company.rfc }}</small></div>
                            </div>

                            <div class="row">
                                <div class="col-lg-8 mb-3 mb-lg-0">
                                    <div class="invoice-items">
                                        <div class="invoice-items__header">
                                            <div><strong>Conceptos facturados</strong><small>{{ lines(bill).length }} concepto(s)</small></div>
                                        </div>
                                        <div v-if="!lines(bill).length" class="invoice-items__empty">Sin conceptos guardados.</div>
                                        <div v-else class="table-responsive">
                                            <table class="table invoice-items-table">
                                                <thead><tr>
                                                    <th>#</th><th>Concepto y clasificación</th><th class="text-right">Importe</th><th class="text-right">Descuento</th><th class="text-right">Total</th>
                                                </tr></thead>
                                                <tbody>
                                                    <tr v-for="(item, pos) in lines(bill)" :key="item.id || pos">
                                                        <td>{{ pos + 1 }}</td>
                                                        <td>
                                                            <div class="invoice-item-description">
                                                                <strong>{{ item.description }}</strong>
                                                                <div class="invoice-item-facts">
                                                                    <span v-if="item.extra_due_item_id">Extra item #{{ item.extra_due_item_id }}</span>
                                                                    <span v-if="item.quantity">{{ item.quantity }} × {{ money(item.unit_price) }} {{ currency(bill) }}</span>
                                                                    <span v-if="item.product_key">SAT {{ item.product_key }}</span>
                                                                    <span v-if="item.unit_key">Unidad {{ item.unit_key }} {{ item.unit_name || '' }}</span>
                                                                    <span v-if="item.taxability">Obj. impuesto {{ item.taxability }}</span>
                                                                </div>
                                                                <div class="invoice-item-taxes">
                                                                    <span v-if="num(item.iva_amount)" class="invoice-tax-chip">IVA {{ money(item.iva_amount) }}</span>
                                                                    <span v-if="num(item.ieps_amount)" class="invoice-tax-chip">IEPS {{ money(item.ieps_amount) }}</span>
                                                                    <span v-if="num(item.isr_ret_amount)" class="invoice-tax-chip invoice-tax-chip--retention">ISR ret. {{ money(item.isr_ret_amount) }}</span>
                                                                    <span v-if="num(item.iva_ret_amount)" class="invoice-tax-chip invoice-tax-chip--retention">IVA ret. {{ money(item.iva_ret_amount) }}</span>
                                                                    <span v-if="!num(item.iva_amount) && !num(item.ieps_amount) && !num(item.isr_ret_amount) && !num(item.iva_ret_amount)" class="invoice-tax-chip invoice-tax-chip--none">Sin impuestos registrados</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td class="text-right">{{ money(lineAmount(item)) }}</td>
                                                        <td class="text-right">{{ money(item.discount) }}</td>
                                                        <td class="text-right"><strong>{{ money(item.total) }}</strong><small class="invoice-currency-code">{{ currency(bill) }}</small></td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-lg-4">
                                    <div class="invoice-totals">
                                        <div class="invoice-totals__header"><i class="fas fa-calculator mr-2"></i> Resumen fiscal</div>
                                        <div class="invoice-totals__row"><span>Importe bruto</span><strong>{{ money(totalItems(bill, 'final_amount')) }}</strong></div>
                                        <div v-if="totalItems(bill, 'discount')" class="invoice-totals__row"><span>Descuentos</span><strong>- {{ money(totalItems(bill, 'discount')) }}</strong></div>
                                        <div class="invoice-totals__row"><span>Subtotal</span><strong>{{ money(invoiceSubtotal(bill)) }}</strong></div>
                                        <div v-if="totalItems(bill, 'ieps_amount')" class="invoice-totals__row"><span>IEPS</span><strong>+ {{ money(totalItems(bill, 'ieps_amount')) }}</strong></div>
                                        <div v-if="totalItems(bill, 'iva_amount')" class="invoice-totals__row"><span>IVA</span><strong>+ {{ money(totalItems(bill, 'iva_amount')) }}</strong></div>
                                        <div v-if="totalItems(bill, 'isr_ret_amount')" class="invoice-totals__row invoice-totals__row--retention"><span>ISR retenido</span><strong>- {{ money(totalItems(bill, 'isr_ret_amount')) }}</strong></div>
                                        <div v-if="totalItems(bill, 'iva_ret_amount')" class="invoice-totals__row invoice-totals__row--retention"><span>IVA retenido</span><strong>- {{ money(totalItems(bill, 'iva_ret_amount')) }}</strong></div>
                                        <div class="invoice-totals__total"><span>Total CFDI</span><strong>{{ money(invoiceTotal(bill)) }} {{ currency(bill) }}</strong></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="invoice-record__footer">
                            <div>
                                <button v-if="status(bill) === 'valid'" type="button"
                                    class="btn invoice-btn invoice-btn--danger" :disabled="send" @click="cancelInvoice(bill)">
                                    <i :class="cancellingBillId === bill.id ? 'fa-solid fa-circle-notch fa-spin' : 'fa-solid fa-ban'" class="mr-1"></i>
                                    {{ cancellingBillId === bill.id ? 'Solicitando...' : 'Cancelar factura' }}
                                </button>
                                <small v-else-if="status(bill) === 'pending'" class="invoice-waiting">
                                    <i class="fa-solid fa-clock mr-1"></i> Esperando confirmación del SAT
                                </small>
                            </div>
                            <div class="invoice-record__documents">
                                <a class="btn invoice-btn invoice-btn--pdf" :href="fileUrl(bill, 'pdf')"
                                    target="_blank" rel="noopener" title="Descargar PDF">
                                    <i class="fas fa-file-pdf mr-1"></i> Descargar PDF
                                </a>
                                <a class="btn invoice-btn invoice-btn--xml" :href="fileUrl(bill, 'xml')"
                                    target="_blank" rel="noopener" title="Descargar XML">
                                    <i class="fas fa-file-code mr-1"></i> Descargar XML
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="invoice-history-modal__footer">
                    <div class="invoice-history-modal__footer-info"><i class="fas fa-info-circle mr-2"></i>
                        El estado de cancelación pendiente no libera conceptos para refacturar.
                    </div>
                    <button type="button" class="btn invoice-btn invoice-btn--secondary" :disabled="send" @click="closeModal">
                        <i class="fas fa-arrow-left mr-1"></i> Regresar
                    </button>
                </div>
            </div>
        </div>
    </modal>
</template>

<style scoped>
.invoice-history-modal {
    --primary: #5672a5;
    --primary-dark: #304d7d;
    --primary-soft: #eef3fb;
    --success: #269b74;
    --success-soft: #eaf8f3;
    --warning: #d99b24;
    --warning-soft: #fff8e8;
    --danger: #d84d5d;
    --danger-soft: #fff0f2;
    --border: #dfe5ee;
    --surface: #ffffff;
    --background: #f5f7fb;
    --text: #1f2937;
    --muted: #6b7280;

    min-height: 360px;

    color: var(--text);
    background: var(--background);
}


/* HEADER */

.invoice-history-modal__header {
    display: flex;

    align-items: center;
    justify-content: space-between;

    gap: 18px;

    padding: 18px 22px;

    color: #ffffff;

    background:
        linear-gradient(
            135deg,
            var(--primary-dark),
            var(--primary)
        );
}


.invoice-history-modal__title-group {
    display: flex;

    align-items: center;

    gap: 14px;

    min-width: 0;
}


.invoice-history-modal__icon {
    display: flex;

    align-items: center;
    justify-content: center;

    flex: 0 0 46px;

    width: 46px;
    height: 46px;

    color: #ffffff;

    background:
        rgba(
            255,
            255,
            255,
            0.15
        );

    border:
        1px solid
        rgba(
            255,
            255,
            255,
            0.2
        );

    border-radius: 12px;

    
}


.invoice-history-modal__title {
    margin: 0;

    color: #ffffff;

    
    font-weight: 700;
}


.invoice-history-modal__subtitle {
    margin-top: 3px;

    color:
        rgba(
            255,
            255,
            255,
            0.78
        );

    
    line-height: 1.45;
}


.invoice-history-modal__header-actions {
    display: flex;

    align-items: center;

    gap: 10px;
}


.invoice-history-modal__debt {
    padding: 5px 9px;

    color: #ffffff;

    background:
        rgba(
            255,
            255,
            255,
            0.12
        );

    border:
        1px solid
        rgba(
            255,
            255,
            255,
            0.15
        );

    border-radius: 999px;

    
    font-weight: 700;
}


.invoice-history-modal__close {
    display: flex;

    align-items: center;
    justify-content: center;

    flex: 0 0 36px;

    width: 36px;
    height: 36px;

    padding: 0;

    color: #ffffff;

    background:
        rgba(
            255,
            255,
            255,
            0.12
        );

    border: 0;
    border-radius: 50%;

    cursor: pointer;

    transition: 0.2s ease;
}


.invoice-history-modal__close:hover {
    color: #ffffff;

    background:
        rgba(
            255,
            255,
            255,
            0.24
        );

    transform:
        rotate(90deg);
}


/* LOADING / EMPTY */

.invoice-history-loading,
.invoice-history-empty {
    display: flex;

    flex-direction: column;
    align-items: center;
    justify-content: center;

    min-height: 300px;

    padding: 45px 20px;

    color: var(--muted);

    text-align: center;
}


.invoice-history-loading__spinner,
.invoice-history-empty__icon {
    display: flex;

    align-items: center;
    justify-content: center;

    width: 52px;
    height: 52px;

    margin-bottom: 12px;

    color: var(--primary);

    background: var(--primary-soft);

    border-radius: 50%;

    
}


.invoice-history-loading h5,
.invoice-history-empty h5 {
    color: var(--text);

    
    font-weight: 700;
}


.invoice-history-loading p,
.invoice-history-empty p {
    
}


/* BODY */

.invoice-history-modal__body {
    max-height:
        calc(
            100vh - 190px
        );

    overflow-y: auto;

    padding: 20px 22px;
}


.invoice-history-summary {
    display: grid;

    grid-template-columns:
        repeat(
            3,
            minmax(
                0,
                1fr
            )
        );

    gap: 10px;

    margin-bottom: 14px;
}


.invoice-history-summary__item {
    padding: 10px 12px;

    background: #ffffff;

    border:
        1px solid
        var(--border);

    border-radius: 9px;
}


.invoice-history-summary__item small,
.invoice-history-summary__item strong {
    display: block;
}


.invoice-history-summary__item small {
    margin-bottom: 2px;

    color: var(--muted);

    

    text-transform: uppercase;
}


.invoice-history-summary__item strong {
    color: var(--text);
}


/* LIST */

.invoice-history-list {
    display: grid;

    gap: 14px;
}


.invoice-record {
    overflow: hidden;

    background: var(--surface);

    border:
        1px solid
        var(--border);

    border-radius: 12px;

    box-shadow:
        0
        3px
        10px
        rgba(
            15,
            23,
            42,
            0.04
        );
}


.invoice-record__header {
    display: flex;

    align-items: center;
    justify-content: space-between;

    gap: 14px;

    padding: 13px 15px;

    background: #fafbfd;

    border-bottom:
        1px solid
        var(--border);
}


.invoice-record__identity {
    display: flex;

    align-items: center;

    gap: 11px;

    min-width: 0;
}


.invoice-record__number {
    display: flex;

    align-items: center;
    justify-content: center;

    flex: 0 0 34px;

    width: 34px;
    height: 34px;

    color: var(--primary-dark);

    background: var(--primary-soft);

    border-radius: 8px;

    
    font-weight: 700;
}


.invoice-record__heading {
    min-width: 0;
}


.invoice-record__title-row {
    display: flex;

    flex-wrap: wrap;
    align-items: center;

    gap: 8px;
}


.invoice-record__title-row strong {
    color: var(--text);

    
}


.invoice-record__heading > small {
    display: block;

    margin-top: 3px;

    color: var(--muted);

    
}


.invoice-uuid {
    word-break: break-all;
}


.invoice-record__date {
    flex: 0 0 auto;

    text-align: right;
}


.invoice-record__date small,
.invoice-record__date strong {
    display: block;
}


.invoice-record__date small {
    color: var(--muted);

    
}


.invoice-record__date strong {
    margin-top: 2px;

    color: var(--text);

    
}


/* STATUS */

.invoice-status {
    display: inline-flex;

    align-items: center;

    padding: 4px 7px;

    border-radius: 999px;

    
    font-weight: 700;
}


.invoice-status--valid {
    color: var(--success);

    background: var(--success-soft);
}


.invoice-status--canceled {
    color: var(--danger);

    background: var(--danger-soft);
}


.invoice-status--pending {
    color: #946b10;

    background: var(--warning-soft);
}


/* RECORD BODY */

.invoice-record__body {
    padding: 15px;
}


.invoice-info-grid {
    display: grid;

    grid-template-columns:
        repeat(
            5,
            minmax(
                0,
                1fr
            )
        );

    gap: 9px;

    margin-bottom: 11px;
}


.invoice-info-card {
    display: flex;

    align-items: center;

    gap: 9px;

    min-width: 0;

    padding: 9px 10px;

    background: #f8fafc;

    border:
        1px solid
        #e8ecf1;

    border-radius: 8px;
}


.invoice-info-card--wide {
    grid-column:
        span 2;
}


.invoice-info-card__icon {
    display: flex;

    align-items: center;
    justify-content: center;

    flex: 0 0 30px;

    width: 30px;
    height: 30px;

    color: var(--primary);

    background: #ffffff;

    border-radius: 7px;

    
}


.invoice-info-card > div {
    min-width: 0;
}


.invoice-info-card small,
.invoice-info-card strong,
.invoice-info-card div > span {
    display: block;
}


.invoice-info-card small {
    color: var(--muted);

    

    text-transform: uppercase;
}


.invoice-info-card strong {
    overflow: hidden;

    margin-top: 1px;

    color: var(--text);

    

    text-overflow: ellipsis;
    white-space: nowrap;
}


.invoice-info-card div > span {
    overflow: hidden;

    margin-top: 1px;

    color: var(--muted);

    

    text-overflow: ellipsis;
    white-space: nowrap;
}


/* PAYMENT */

.invoice-payment-info {
    display: grid;

    grid-template-columns:
        repeat(
            2,
            minmax(
                0,
                1fr
            )
        );

    gap: 9px;

    margin-bottom: 13px;

    padding: 10px 12px;

    background: #fbfcfd;

    border:
        1px solid
        var(--border);

    border-radius: 8px;
}


.invoice-payment-info small,
.invoice-payment-info strong {
    display: block;
}


.invoice-payment-info small {
    color: var(--muted);

    

    text-transform: uppercase;
}


.invoice-payment-info strong {
    margin-top: 2px;

    color: var(--text);

    
}


/* ITEMS */

.invoice-items {
    height: 100%;

    overflow: hidden;

    border:
        1px solid
        var(--border);

    border-radius: 9px;
}


.invoice-items__header {
    display: flex;

    align-items: center;
    justify-content: space-between;

    padding: 9px 12px;

    background: #fafbfd;

    border-bottom:
        1px solid
        var(--border);
}


.invoice-items__header strong,
.invoice-items__header small {
    display: block;
}


.invoice-items__header strong {
    color: var(--text);

    
}


.invoice-items__header small {
    margin-top: 1px;

    color: var(--muted);

    
}


.invoice-items__empty {
    padding: 30px 15px;

    color: var(--muted);

    

    text-align: center;
}


.invoice-items-table {
    margin: 0;

    
}


.invoice-items-table thead th {
    padding: 8px 10px;

    color: #4b5563;

    background: #f8fafc;

    border-top: 0;
    border-bottom:
        1px solid
        var(--border);

    

    text-transform: uppercase;
}


.invoice-items-table tbody td {
    padding: 9px 10px;

    vertical-align: middle;

    border-top:
        1px solid
        #eef1f4;
}


.invoice-item-index {
    width: 45px;

    color: var(--muted);

    text-align: center;
}


.invoice-item-description strong {
    display: block;

    color: var(--text);

    
}


.invoice-item-taxes {
    display: flex;

    flex-wrap: wrap;

    gap: 4px;

    margin-top: 5px;
}


.invoice-tax-chip {
    display: inline-flex;

    padding: 2px 5px;

    color: var(--primary-dark);

    background: var(--primary-soft);

    border-radius: 999px;

    
    font-weight: 600;
}


.invoice-tax-chip--retention {
    color: #a23845;

    background: var(--danger-soft);
}


.invoice-tax-chip--none {
    color: var(--muted);

    background: #f1f3f5;
}


.invoice-currency-code {
    display: block;

    margin-top: 1px;

    color: var(--muted);

    
}


/* TOTALS */

.invoice-totals {
    overflow: hidden;

    border:
        1px solid
        var(--border);

    border-radius: 9px;
}


.invoice-totals__header {
    display: flex;

    align-items: center;

    padding: 9px 11px;

    color: #ffffff;

    background: var(--primary-dark);

    
    font-weight: 700;
}


.invoice-totals__row,
.invoice-totals__total {
    display: flex;

    align-items: center;
    justify-content: space-between;

    gap: 10px;

    padding: 8px 11px;

    border-bottom:
        1px solid
        #edf0f4;

    
}


.invoice-totals__row span {
    color: var(--muted);
}


.invoice-totals__row strong {
    color: var(--text);

    
}


.invoice-totals__row--retention {
    background: #fffafb;
}


.invoice-totals__row--retention strong {
    color: var(--danger);
}


.invoice-totals__total {
    border-bottom: 0;

    color: var(--primary-dark);

    background: var(--primary-soft);
}


.invoice-totals__total span {
    font-weight: 600;
}


.invoice-totals__total strong {
    color: var(--primary-dark);

    
}


/* FOOTER RECORD */

.invoice-record__footer {
    display: flex;

    align-items: center;
    justify-content: space-between;

    gap: 10px;

    padding: 11px 15px;

    background: #fafbfd;

    border-top:
        1px solid
        var(--border);
}


.invoice-record__documents {
    display: flex;

    flex-wrap: wrap;

    gap: 6px;

    justify-content: flex-end;
}


/* BUTTONS */

.invoice-btn {
    min-height: 34px;

    padding: 6px 10px;

    border-radius: 6px;

    
    font-weight: 600;
}


.invoice-btn--primary {
    color: var(--primary-dark);

    background: var(--primary-soft);

    border:
        1px solid
        #d8e2f1;
}


.invoice-btn--primary:hover {
    color: #ffffff;

    background: var(--primary);

    border-color: var(--primary);
}


.invoice-btn--pdf {
    color: #a23845;

    background: #fff8f9;

    border:
        1px solid
        #f2d4d9;
}


.invoice-btn--pdf:hover {
    color: #ffffff;

    background: #b94958;

    border-color: #b94958;
}


.invoice-btn--xml {
    color: #4b5563;

    background: #ffffff;

    border:
        1px solid
        var(--border);
}


.invoice-btn--xml:hover {
    color: var(--text);

    background: #f3f5f8;
}


.invoice-btn--danger {
    color: var(--danger);

    background: #ffffff;

    border:
        1px solid
        #ecc8cd;
}


.invoice-btn--danger:hover {
    color: #ffffff;

    background: var(--danger);

    border-color: var(--danger);
}


.invoice-btn--secondary {
    min-width: 110px;

    color: #4b5563;

    background: #ffffff;

    border:
        1px solid
        var(--border);
}


.invoice-btn--secondary:hover {
    color: var(--text);

    background: #f3f5f8;
}


/* MAIN FOOTER */

.invoice-history-modal__footer {
    display: flex;

    align-items: center;
    justify-content: space-between;

    gap: 12px;

    padding: 13px 22px;

    background: #ffffff;

    border-top:
        1px solid
        var(--border);
}


.invoice-history-modal__footer-info {
    display: flex;

    align-items: center;

    color: var(--muted);

    
}


.invoice-history-modal__footer-info i {
    color: var(--primary);
}


/* RESPONSIVE */

@media (max-width: 1200px) {

    .invoice-info-grid {
        grid-template-columns:
            repeat(
                3,
                minmax(
                    0,
                    1fr
                )
            );
    }


    .invoice-info-card--wide {
        grid-column:
            span 2;
    }
}


@media (max-width: 768px) {

    .invoice-history-modal__header {
        padding: 15px;
    }


    .invoice-history-modal__subtitle,
    .invoice-history-modal__debt {
        display: none;
    }


    .invoice-history-modal__body {
        padding: 13px;
    }


    .invoice-history-summary {
        grid-template-columns:
            1fr;
    }


    .invoice-record__header {
        align-items: flex-start;

        flex-direction: column;
    }


    .invoice-record__date {
        margin-left: 45px;

        text-align: left;
    }


    .invoice-info-grid {
        grid-template-columns:
            1fr;
    }


    .invoice-info-card--wide {
        grid-column:
            span 1;
    }


    .invoice-payment-info {
        grid-template-columns:
            1fr;
    }


    .invoice-record__footer,
    .invoice-history-modal__footer {
        align-items: stretch;

        flex-direction: column;
    }


    .invoice-record__documents {
        flex-direction: column;
    }


    .invoice-record__documents .invoice-btn,
    .invoice-record__footer > div:first-child .invoice-btn,
    .invoice-history-modal__footer .invoice-btn {
        width: 100%;
    }


    .invoice-history-modal__footer-info {
        display: none;
    }
}

.invoice-history-modal--independent .invoice-item-facts { display: flex; flex-wrap: wrap; gap: 3px 12px; margin: 5px 0; color: #65758b; font-size: 10px; }
.invoice-history-modal--independent .invoice-payment-info--extended { grid-template-columns: repeat(3, minmax(0,1fr)); }
.invoice-history-modal--independent .invoice-waiting { color: #946b10; font-weight: 600; }
.invoice-history-modal--independent .invoice-info-card strong { white-space: normal; overflow-wrap: anywhere; }
.invoice-history-modal--independent .invoice-record__documents .invoice-btn { text-decoration: none; }
@media(max-width:768px) { .invoice-history-modal--independent .invoice-payment-info--extended { grid-template-columns: 1fr; } }
</style>
